import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Chip } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDate, formatMoney, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Dialog, Field, Panel, PanelRow, fieldClass } from '../../ui';
import { STATUS_TONE, grossOf } from './Contracts';

/**
 * The contract panel (10-web-admin § 14).
 *
 * The "Plätze" section is the P1 half of office contracts: seats are handed
 * out by e-mail, and the person gets access once they sign in with exactly
 * that address (BR-ACC-05). It only appears where there are seats to hand out,
 * which is an office contract and nothing else.
 */
export function ContractPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [terminating, setTerminating] = useState(false);
  const [endDay, setEndDay] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: qk.admin.area('contracts') });
    void queryClient.invalidateQueries({ queryKey: qk.admin.area('contract-seats', id) });
  };
  const onMutationError = (error: unknown) => setProblem(error instanceof ApiProblem ? error : null);

  const grantSeat = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/contracts/{id}/seats', {
        params: { path: { id } },
        body: { email: email.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setEmail('');
      refresh();
    },
    onError: onMutationError,
  });

  const revokeSeat = useMutation({
    mutationFn: async (seatId: string) => {
      const { error } = await api.DELETE('/admin/contracts/{id}/seats/{seatId}', {
        params: { path: { id, seatId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      refresh();
    },
    onError: onMutationError,
  });

  const terminate = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/contracts/{id}/terminate', {
        params: { path: { id } },
        body: { endDay, reason: reason.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setTerminating(false);
      refresh();
    },
    onError: onMutationError,
  });

  const contract = useQuery({
    queryKey: qk.admin.area('contracts', { id }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/contracts', { params: { query: {} } });
      if (error) throw error;
      return data.items.find((item) => item.id === id) ?? null;
    },
  });

  const seats = useQuery({
    queryKey: qk.admin.area('contract-seats', id),
    enabled: contract.data?.productKey === 'office',
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/contracts/{id}/seats', {
        params: { path: { id } },
      });
      if (error) throw error;
      return data;
    },
  });

  const data = contract.data;
  if (!data)
    return <Panel label={t('admin.contracts.panelLabel')} title="—" onClose={onClose} children={null} />;

  const full = seats.data ? seats.data.used >= (seats.data.includedSeats ?? 0) : false;

  return (
    <Panel
      label={t('admin.contracts.panelLabel', { number: data.number ?? '' })}
      title={
        data.resourceLabel
          ? [data.resourceLabel, data.planName].filter(Boolean).join(' · ')
          : t(`admin.members.product.${data.productKey}`)
      }
      subtitle={[data.payerName, data.customerNumber, formatDate(data.startDay)].filter(Boolean).join(' · ')}
      onClose={onClose}
    >
      <div className="flex items-center gap-2">
        <Chip tone={STATUS_TONE[data.status] ?? 'neutral'} label={t(`admin.contracts.chip.${data.status}`)} />
        <Chip tone="teal" label={t(`admin.members.product.${data.productKey}`)} />
      </div>

      <div>
        <PanelRow
          label={t('admin.contracts.monthlyNet')}
          value={data.monthlyNetCents === null ? '—' : formatMoney(data.monthlyNetCents)}
        />
        <PanelRow
          label={t('admin.contracts.monthlyGross')}
          value={
            data.monthlyNetCents === null
              ? '—'
              : t('admin.contracts.grossInclVat', { amount: formatMoney(grossOf(data.monthlyNetCents)) })
          }
        />
        <PanelRow
          label={t('admin.contracts.includedSeats')}
          value={data.includedSeats === null ? '—' : String(data.includedSeats)}
        />
        <PanelRow label={t('admin.contracts.start')} value={formatDate(data.startDay)} />
        <PanelRow
          label={t('admin.contracts.end')}
          value={data.endDay ? formatDate(data.endDay) : t('admin.contracts.openEnded')}
        />
        {data.beneficiaryUserId ? (
          <PanelRow
            label={t('admin.contracts.beneficiary')}
            value={<Link to={`/mitglieder/${data.beneficiaryUserId}`}>{data.beneficiaryName ?? '—'}</Link>}
          />
        ) : null}
      </div>

      {data.productKey === 'office' ? (
        <div className="bg-fog rounded-input flex flex-col gap-2 px-3.5 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-adminBody text-ink font-semibold">{t('admin.contracts.seats')}</p>
            <p className="text-adminSmall text-muted">
              {t('admin.contracts.seatsOf', {
                used: seats.data?.used ?? 0,
                total: seats.data?.includedSeats ?? 0,
              })}
            </p>
          </div>

          {(seats.data?.seats ?? []).length === 0 ? (
            <p className="text-adminSmall text-muted">{t('admin.contracts.noSeats')}</p>
          ) : (
            <ul className="text-adminSmall flex flex-col gap-1.5">
              {(seats.data?.seats ?? []).map((seat) => (
                <li key={seat.seatId} className="border-hairline flex items-center gap-2 border-b pb-1.5">
                  <span className="flex-1">{[seat.name, seat.email].filter(Boolean).join(' · ')}</span>
                  <Chip
                    tone={seat.status === 'active' ? 'success' : 'warn'}
                    label={
                      seat.status === 'active'
                        ? t('admin.contracts.seatActive')
                        : t('admin.contracts.seatInvited', {
                            date: seat.invitedOn ? formatDate(seat.invitedOn) : '',
                          })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => revokeSeat.mutate(seat.seatId)}
                    className="text-muted hover:text-danger-text font-semibold"
                  >
                    {t('admin.contracts.seatRemove')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2 pt-1">
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t('admin.contracts.seatEmail')}
              type="email"
              disabled={full}
              className="border-line-strong rounded-input text-adminBody h-10 flex-1 border px-3 disabled:bg-white"
            />
            <Button
              size="sm"
              disabled={full || !email.includes('@')}
              loading={grantSeat.isPending}
              onClick={() => grantSeat.mutate()}
            >
              {t('admin.contracts.grantSeat')}
            </Button>
          </div>
          <p className="text-adminSmall text-muted">
            {full ? t('admin.contracts.seatsFull') : t('admin.contracts.seatHint')}
          </p>
        </div>
      ) : null}

      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      {data.status === 'ended' || data.status === 'terminated' ? null : (
        <Button size="sm" variant="danger" className="mt-auto" onClick={() => setTerminating(true)}>
          {t('admin.contracts.terminate')}
        </Button>
      )}

      {terminating ? (
        <Dialog
          title={t('admin.contracts.terminateTitle')}
          intro={t('admin.contracts.terminateBody')}
          tone="danger"
          confirmLabel={t('admin.contracts.terminate')}
          onConfirm={() => terminate.mutate()}
          onClose={() => setTerminating(false)}
          busy={terminate.isPending}
          disabled={reason.trim().length < 5}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <Field label={t('admin.contracts.terminateOn')}>
            <input
              type="date"
              value={endDay}
              onChange={(event) => setEndDay(event.target.value)}
              className={fieldClass}
            />
          </Field>
          <Field label={t('admin.contracts.terminateReason')} hint={t('admin.reasonHint')}>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('admin.reasonPlaceholder')}
              className={fieldClass}
            />
          </Field>
        </Dialog>
      ) : null}
    </Panel>
  );
}
